import { useEffect, useState } from 'react';
import { useForm } from '@mantine/form';
import { Alert, Box, Button, Group, Loader, NumberInput, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle } from '@tabler/icons-react';
import { DECIMAL_INPUT_PROPS } from '../../components/form/numberFieldPresets';
import { settingsApi } from '../../services/settingsApi';
import { getApiErrorMessage, getApiViolations } from '../../utils/errorHandling';
import { mapViolations } from '../../utils/mapViolations';

interface AnnualWorkingHoursFormValues {
  annualHours: number | '';
}

interface AnnualWorkingHoursPanelProps {
  /** Meldet die Zeilenzahl dieses Bereichs an `SettingsPage` (Navigationszähler, Sketch 018) —
   *  bei der KANÜ-Einstellung immer 1, gehalten für dieselbe Architektur wie der künftige
   *  Umweltdaten-Bereich (33-07), dessen Zeilenzahl sich tatsächlich ändert. */
  onCountChange?: (count: number) => void;
}

/** DECIMAL-String der API in einen Wert für `NumberInput` übersetzen — aus `SettingsPage.tsx`
 *  hierher gezogen (33-06, D-20-Umbau); einziger Verbraucher bleibt dieser Bereich. */
function toInputValue(decimal: string | null | undefined): number | '' {
  const parsed = decimal != null && decimal !== '' ? parseFloat(decimal) : NaN;
  return Number.isNaN(parsed) ? '' : parsed;
}

/**
 * KANÜ-Einstellung unter „Unfalldaten" (D-19: eigener Speichern-Knopf, unabhängig vom
 * Aschegehalt-Bereich) — auf `@mantine/form` umgestellt (D-20, Projektregel `@mantine/form`):
 * Felder ausschließlich über `form.getInputProps`, kein eigenes `onChange`; der Speichern-Knopf
 * ist immer aktiv und nur während des Speicherns gesperrt (D-23). Feldfehler ausschließlich vom
 * Backend über `form.setErrors(mapViolations(getApiViolations(err)))`, keine eigene
 * Prüfregel-Konfiguration im Formular.
 */
export function AnnualWorkingHoursPanel({ onCountChange }: AnnualWorkingHoursPanelProps = {}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // controlled: ein einzelnes Feld, kein Anlass fuer einen Moduswechsel (D-23 entfernte die
  // fruehere Begruendung "Knopf haengt an form.isDirty()").
  const form = useForm<AnnualWorkingHoursFormValues>({
    mode: 'controlled',
    initialValues: { annualHours: '' },
  });

  useEffect(() => {
    onCountChange?.(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- die KANÜ-Einstellung ist immer
    // genau EIN Datensatz (settingsApi.getAnnualWorkingHours liest die feste id 1); die Zahl
    // aendert sich nie zur Laufzeit, ein erneuter Aufruf bei jedem Render waere wirkungslos.
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setLoadError(null);
        const setting = await settingsApi.getAnnualWorkingHours();
        if (cancelled) return;
        const next = { annualHours: toInputValue(setting.annualHours) };
        // form.setValues(next) + form.resetDirty(next) halten Wert und Vergleichsstand gleich;
        // kein Knopf liest diesen Stand mehr, der Speichern-Knopf ist nach D-23 immer aktiv.
        form.setValues(next);
        form.resetDirty(next);
      } catch (err) {
        if (!cancelled) {
          setLoadError(getApiErrorMessage(err, 'Fehler beim Laden der Einstellungen'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- laedt genau einmal beim Mounten,
    // `form` ist ueber Mantines useForm-Identitaet stabil.
  }, []);

  async function handleSubmit(values: AnnualWorkingHoursFormValues) {
    setFormError(null);
    setSaving(true);

    try {
      const updated = await settingsApi.updateAnnualWorkingHours(
        values.annualHours === '' ? '' : String(values.annualHours)
      );
      const next = { annualHours: toInputValue(updated.annualHours) };
      form.setValues(next);
      form.resetDirty(next);
      notifications.show({
        title: 'Gespeichert',
        message: 'Die Einstellungen wurden übernommen.',
        color: 'green',
      });
    } catch (err) {
      form.setErrors(mapViolations(getApiViolations(err)));
      setFormError(getApiErrorMessage(err, 'Fehler beim Speichern'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Stack gap="md">
      <div>
        <Title order={3}>Unfalldaten</Title>
        <Text size="sm" c="vdzGray.6">
          Werte, die in die Unfallstatistik einfließen.
        </Text>
      </div>

      {loadError && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" onClose={() => setLoadError(null)} withCloseButton>
          {loadError}
        </Alert>
      )}

      {loading ? (
        <Group justify="center" py="md">
          <Loader size="sm" />
        </Group>
      ) : (
        <Box component="form" onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            {formError && (
              <Alert icon={<IconAlertCircle size={16} />} color="red">
                {formError}
              </Alert>
            )}

            <NumberInput
              label="Jahresarbeitsstunden Kräfte aus Arbeitnehmerüberlassung (KANÜ)"
              description="Der Wert gilt für neu erfasste Meldungen. Bereits gespeicherte Meldungen behalten den Wert, mit dem sie erfasst wurden."
              {...form.getInputProps('annualHours')}
              {...DECIMAL_INPUT_PROPS}
              fixedDecimalScale
              disabled={saving}
              style={{ maxWidth: 420 }}
            />

            <Group justify="flex-start">
              <Button type="submit" disabled={saving} loading={saving}>
                Speichern
              </Button>
            </Group>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
